/**
 * Cartões de contagem por status. Clicar filtra a lista — o número vira ação em
 * vez de enfeite.
 */
import { motion } from "motion/react";
import { STATUS, summarizeFleet, type VehicleStatus } from "../lib/fleet.ts";
import type { Vehicle } from "../lib/schemas.ts";
import { AnimatedNumber } from "./AnimatedNumber";
import { Dot } from "./ui";

/** Ordem de leitura: o que exige ação primeiro. */
const ORDER: VehicleStatus[] = ["backlog", "moving", "idle", "stopped", "offline"];

export function FleetSummary({
  vehicles,
  active,
  onSelect,
}: {
  vehicles: Vehicle[];
  active: VehicleStatus | null;
  onSelect: (status: VehicleStatus | null) => void;
}) {
  const totals = summarizeFleet(vehicles);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <Tile label="Frota" count={totals.total} selected={active === null} onClick={() => onSelect(null)} />
      {ORDER.map((status) => (
        <Tile
          key={status}
          label={STATUS[status].label}
          count={totals[status]}
          dot={STATUS[status].dot}
          selected={active === status}
          onClick={() => onSelect(active === status ? null : status)}
        />
      ))}
    </div>
  );
}

function Tile({
  label,
  count,
  dot,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  dot?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      aria-pressed={selected}
      className={`rounded-xl border px-3 py-2 text-left transition-colors ${
        selected ? "border-accent/60 bg-accent/10" : "border-line bg-panel/60 hover:border-line"
      }`}
    >
      <div className="flex items-center gap-1.5">
        {dot ? <Dot className={dot} /> : null}
        <span className="truncate text-[11px] text-muted">{label}</span>
      </div>
      <AnimatedNumber value={count} className="text-2xl font-semibold tabular-nums text-ink" />
    </motion.button>
  );
}
